import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { SITE, API } from "@/config/site";
import { MessageCircle, Mail } from "lucide-react";

const PLANS = ["Group Program", "1:1 Private Coaching", "Not sure yet"];

export default function Enquiry() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", plan: PLANS[0], message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you — Diego will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", plan: PLANS[0], message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email diego directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquire" data-testid="enquiry-section" className="relative py-28 md:py-40 bg-surface/30 border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-start">
        <Reveal>
          <Overline>Apply to the cohort</Overline>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-tight text-white mt-5">
            Let&apos;s build the skills — and the confidence.
          </h2>
          <p className="text-slate-400 mt-6 leading-relaxed max-w-md">
            Tell me a little about yourself and where you are on your journey.
            I&apos;ll personally reach out to see if the upcoming cohort is the right
            fit for you.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-link"
              className="flex items-center gap-4 group"
            >
              <span className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center text-burgundy group-hover:border-burgundy/50 transition-colors">
                <MessageCircle size={18} />
              </span>
              <span className="text-slate-200 group-hover:text-white transition-colors">Chat on WhatsApp</span>
            </a>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="email-link"
              className="flex items-center gap-4 group"
            >
              <span className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center text-burgundy group-hover:border-burgundy/50 transition-colors">
                <Mail size={18} />
              </span>
              <span className="text-slate-200 group-hover:text-white transition-colors">{SITE.email}</span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="rounded-3xl border border-white/10 bg-ink p-8 md:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name" testid="lead-name">
                <input value={form.name} onChange={set("name")} data-testid="lead-name" className="input-dark" placeholder="Your name" />
              </Field>
              <Field label="Email" testid="lead-email">
                <input value={form.email} onChange={set("email")} type="email" data-testid="lead-email" className="input-dark" placeholder="you@email.com" />
              </Field>
            </div>
            <Field label="Phone / WhatsApp">
              <input value={form.phone} onChange={set("phone")} data-testid="lead-phone" className="input-dark" placeholder="+65 ..." />
            </Field>
            <Field label="Interested in">
              <select value={form.plan} onChange={set("plan")} data-testid="lead-plan" className="input-dark">
                {PLANS.map((p) => <option key={p} className="bg-ink">{p}</option>)}
              </select>
            </Field>
            <Field label="Message">
              <textarea value={form.message} onChange={set("message")} rows={4} data-testid="lead-message" className="input-dark resize-none" placeholder="Tell me about your goals..." />
            </Field>
            <button
              type="submit"
              disabled={loading}
              data-testid="lead-submit"
              className="w-full bg-burgundy hover:bg-burgundy-hover disabled:opacity-60 text-ink font-semibold font-mono text-xs uppercase tracking-[0.2em] py-4 rounded-full transition-colors duration-300"
            >
              {loading ? "Sending…" : "Send application"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);
